import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { X, ChevronRight, ChevronLeft } from "lucide-react";
import { L, t } from "../lib/translations";

const STEPS = [
  {
    id: "welcome",
    target: null,
    side: null,
    content: {
      en: { title: "Welcome to iFollowers! 🎉", desc: "Here's a quick tour so you know exactly what you can do here." },
      ru: { title: "Добро пожаловать в iFollowers! 🎉", desc: "Быстрый тур — покажем всё самое важное." },
      he: { title: "!ברוך הבא ל-iFollowers 🎉", desc: ".סיור קצר כדי שתדע בדיוק מה אפשר לעשות כאן" },
    },
  },
  {
    id: "tasks",
    target: "[data-testid='tour-tasks-link']",
    side: "below",
    content: {
      en: { title: "🎯 Earn iPoints with Tasks", desc: "Complete short tasks — follow accounts, like posts — and rack up iPoints you can spend on Instagram growth." },
      ru: { title: "🎯 Задания → iPoints", desc: "Выполняй короткие задания — подписывайся, ставь лайки — и копи iPoints на прокачку профиля." },
      he: { title: "🎯 משימות → iPoints", desc: "השלם משימות קצרות — עקוב, לייק — וצבור iPoints שתוכל להשתמש בהם לצמיחה." },
    },
  },
  {
    id: "free-gifts",
    target: "[data-testid='free-gift-card']",
    side: "above",
    content: {
      en: { title: "🎁 Your one-time welcome bonus", desc: "Grab 50 free likes for any post and 10 free followers — right here. Just paste your Instagram link." },
      ru: { title: "🎁 Твой приветственный бонус", desc: "50 бесплатных лайков на пост и 10 подписчиков — здесь. Вставь ссылку на Instagram." },
      he: { title: "🎁 בונוס הקבלת פנים שלך", desc: "50 לייקים חינם לכל פוסט ו-10 עוקבים — ממש כאן. רק הדבק קישור לאינסטגרם שלך." },
    },
  },
  {
    id: "shop",
    target: "[data-testid='tour-shop-card']",
    side: "below",
    content: {
      en: { title: "🛍️ Buy followers, likes & views", desc: "When you're ready to grow fast — pick a package and get Instagram services delivered within minutes." },
      ru: { title: "🛍️ Подписчики, лайки, просмотры", desc: "Готов расти быстро? Выбери пакет — доставка Instagram-услуг в течение минут." },
      he: { title: "🛍️ עוקבים, לייקים וצפיות", desc: "מוכן לצמיחה מהירה? בחר חבילה — שירותי אינסטגרם יגיעו תוך דקות." },
    },
  },
  {
    id: "topup",
    target: "[data-testid='tour-topup-card']",
    side: "below",
    content: {
      en: { title: "💳 Add iPoints to your balance", desc: "Top up with bank transfer or card. iPoints never expire — spend them on any service, any time." },
      ru: { title: "💳 Пополни баланс iPoints", desc: "Пополнение через перевод или карту. iPoints не сгорают — трать на любую услугу в любое время." },
      he: { title: "💳 הוסף iPoints ליתרה שלך", desc: "טען בהעברה בנקאית או כרטיס. iPoints לא פגים — השתמש בהם בכל שירות, בכל עת." },
    },
  },
];

const TOOLTIP_W  = 310;
const TOOLTIP_H  = 240; // estimated height for placement math
const SPOT_PAD   = 10;

// ── C-curve arrow ─────────────────────────────────────────────────────────────
function makeArrow(sx, sy, ex, ey) {
  const dx = ex - sx, dy = ey - sy;
  const dist = Math.hypot(dx, dy);
  if (dist < 4) return null;
  const perpX = -dy / dist, perpY = dx / dist;
  const arc = Math.max(80, dist * 0.58);
  const cp1x = sx + dx * 0.28 + perpX * arc;
  const cp1y = sy + dy * 0.28 + perpY * arc;
  const cp2x = ex - dx * 0.28 + perpX * arc * 0.55;
  const cp2y = ey - dy * 0.28 + perpY * arc * 0.55;
  return `M ${sx.toFixed(1)} ${sy.toFixed(1)} C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${ex.toFixed(1)} ${ey.toFixed(1)}`;
}

// ── Tooltip placement ─────────────────────────────────────────────────────────
function placeTooltip(tr, side, vw, vh) {
  const isMobile = vw < 640;
  const TW = isMobile ? vw - 32 : Math.min(TOOLTIP_W, vw - 32);

  // ── MOBILE: anchor tooltip to top or bottom of screen ──
  if (isMobile) {
    const NAVBAR_H = 68;
    const BOTTOM_GAP = 16;
    const targetCenterY = tr.y + tr.height / 2;
    // Place tooltip at bottom when target is in top 55% of screen, else at top
    const useBottom = targetCenterY < vh * 0.55;
    const tx = 16;
    // Arrow tip always lands near the TOP of the target card so it's never
    // hidden behind the tooltip and the distance is always meaningful.
    const tipY = tr.y + Math.min(22, tr.height * 0.15);
    const tipX = tr.x + tr.width / 2;

    if (useBottom) {
      const ty = vh - TOOLTIP_H - BOTTOM_GAP;
      // Start from upper-right corner of tooltip so the arc is a nice diagonal
      const sx = tx + TW * 0.68;
      const sy = ty - 12;
      return { tx, ty, TW, arrow: makeArrow(sx, sy, tipX, tipY) };
    } else {
      const ty = NAVBAR_H + 8;
      // Start from lower-left corner of tooltip for a mirrored diagonal
      const sx = tx + TW * 0.32;
      const sy = ty + TOOLTIP_H + 12;
      return { tx, ty, TW, arrow: makeArrow(sx, sy, tipX, tipY) };
    }
  }

  // ── DESKTOP ──
  let tx, ty, sx, sy, ex, ey;

  if (side === "below-far") {
    const gap = Math.min(190, vh * 0.23);
    ty = Math.min(tr.y + tr.height + gap, vh - TOOLTIP_H - 16);
    tx = Math.max(16, Math.min(tr.x + tr.width / 2 - TW / 2, vw - TW - 16));
    sx = tx + TW / 2;  sy = ty;
    ex = tr.x + tr.width / 2;  ey = tr.y + tr.height + 4;

  } else if (side === "below") {
    ty = Math.min(tr.y + tr.height + 90, vh - TOOLTIP_H - 16);
    tx = Math.max(16, Math.min(tr.x + tr.width / 2 - TW / 2, vw - TW - 16));
    sx = tx + TW / 2;  sy = ty;
    ex = tr.x + tr.width / 2;  ey = tr.y + tr.height + 4;

  } else if (side === "above") {
    ty = Math.max(80, tr.y - TOOLTIP_H - 90);
    tx = Math.max(16, Math.min(tr.x + tr.width / 2 - TW / 2, vw - TW - 16));
    sx = tx + TW / 2;  sy = ty + TOOLTIP_H;
    ex = tr.x + tr.width / 2;  ey = tr.y - 4;

  } else if (side === "right") {
    const gap = 70;
    let flip = false;
    tx = tr.x + tr.width + gap;
    if (tx + TW + 16 > vw) { tx = tr.x - TW - gap; flip = true; }
    tx = Math.max(16, Math.min(tx, vw - TW - 16));
    ty = Math.max(80, Math.min(tr.y + tr.height / 2 - TOOLTIP_H / 2, vh - TOOLTIP_H - 16));
    sx = flip ? tx + TW : tx;
    sy = ty + TOOLTIP_H / 2;
    ex = flip ? tr.x - 4 : tr.x + tr.width + 4;
    ey = tr.y + tr.height / 2;

  } else { // left
    const gap = 70;
    let flip = false;
    tx = tr.x - TW - gap;
    if (tx < 16) { tx = tr.x + tr.width + gap; flip = true; }
    tx = Math.max(16, Math.min(tx, vw - TW - 16));
    ty = Math.max(80, Math.min(tr.y + tr.height / 2 - TOOLTIP_H / 2, vh - TOOLTIP_H - 16));
    sx = flip ? tx : tx + TW;
    sy = ty + TOOLTIP_H / 2;
    ex = flip ? tr.x + tr.width + 4 : tr.x - 4;
    ey = tr.y + tr.height / 2;
  }

  return { tx, ty, TW, arrow: makeArrow(sx, sy, ex, ey) };
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function OnboardingTour({ onDone }) {
  const { user }  = useAuth();
  const { lang }  = useLanguage();
  const [step, setStep]     = useState(0);
  const [layout, setLayout] = useState(null);
  const [visible, setVisible] = useState(false);
  const isRtl = lang === "he";
  const cur = STEPS[step];

  // Stable preventDefault ref for scroll locking
  const stopScroll = useRef((e) => { try { e.preventDefault(); } catch {} });

  const lockScroll = useCallback(() => {
    document.addEventListener("wheel",      stopScroll.current, { passive: false });
    document.addEventListener("touchmove",  stopScroll.current, { passive: false });
  }, []);

  const unlockScroll = useCallback(() => {
    document.removeEventListener("wheel",     stopScroll.current);
    document.removeEventListener("touchmove", stopScroll.current);
  }, []);

  const done = useCallback(() => {
    if (user?.user_id) localStorage.setItem(`welcomed_v2_${user.user_id}`, "1");
    unlockScroll();
    window.dispatchEvent(new Event('tour:close-mobile-nav'));
    onDone?.();
  }, [user, onDone, unlockScroll]);

  // Per-step effect: scroll into view → lock scroll → compute layout → show
  useEffect(() => {
    let cancelled = false;
    setVisible(false);
    unlockScroll(); // unlock while we (possibly) scroll

    const raf2 = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

    const run = async () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isMobile = vw < 640;
      const isNavTarget = /data-testid='nav-/.test(cur.target);

      const showCenter = () => {
        if (!cancelled) {
          setLayout({ center: true, TW: Math.min(380, vw - 40) });
          lockScroll();
          raf2(() => { if (!cancelled) setVisible(true); });
        }
      };

      // Center modal (welcome or no target)
      if (!cur.target || !cur.side) {
        if (isMobile) window.dispatchEvent(new Event('tour:close-mobile-nav'));
        showCenter();
        return;
      }

      let targetSelector = cur.target;

      // Nav items are hidden on mobile (md:flex) — open hamburger menu
      if (isMobile && isNavTarget) {
        const mobileSelector = cur.target.replace(/\[data-testid='([^']+)'\]/, "[data-testid='$1-mobile']");
        window.dispatchEvent(new Event('tour:open-mobile-nav'));
        await new Promise(res => setTimeout(res, 380));
        if (cancelled) return;
        const mobileEl = document.querySelector(mobileSelector);
        if (mobileEl && mobileEl.getBoundingClientRect().width > 0) {
          targetSelector = mobileSelector;
        } else {
          window.dispatchEvent(new Event('tour:close-mobile-nav'));
          showCenter();
          return;
        }
      } else if (isMobile) {
        // Non-nav step on mobile — close hamburger if it was open from previous step
        window.dispatchEvent(new Event('tour:close-mobile-nav'));
      }

      // Find element; retry if not yet in DOM (conditional rendering / API loading)
      let el = document.querySelector(targetSelector);
      if (!el || el.getBoundingClientRect().width === 0 || el.getBoundingClientRect().height === 0) {
        for (let i = 0; i < 5 && !cancelled; i++) {
          await new Promise(res => setTimeout(res, 350));
          if (cancelled) return;
          const re = document.querySelector(targetSelector);
          if (re) {
            const rr = re.getBoundingClientRect();
            if (rr.width > 0 && rr.height > 0) { el = re; break; }
          }
        }
      }

      if (!el || el.getBoundingClientRect().width === 0) {
        showCenter();
        return;
      }

      const r0 = el.getBoundingClientRect();

      // Scroll element into view if off-screen (mobile dashboard cards below the fold)
      const offTop    = r0.top < 80;
      const offBottom = r0.bottom > vh - 80;
      if (offTop || offBottom) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Wait until the scroll animation fully stops (3 identical frames) or 750ms
        await new Promise(resolve => {
          let prev = window.scrollY, same = 0;
          const check = () => {
            if (cancelled) { resolve(); return; }
            const cur = window.scrollY;
            same = (cur === prev) ? same + 1 : 0;
            prev = cur;
            if (same >= 3) resolve(); else requestAnimationFrame(check);
          };
          requestAnimationFrame(check);
          setTimeout(resolve, 750);
        });
        if (cancelled) return;
      }

      lockScroll();

      // Re-measure after scroll
      const r = el.getBoundingClientRect();
      const targetRect = { x: r.left, y: r.top, width: r.width, height: r.height };
      const placed = placeTooltip(targetRect, cur.side, vw, vh);

      if (!cancelled) {
        setLayout({ targetRect, ...placed });
        raf2(() => { if (!cancelled) setVisible(true); });
      }
    };

    const t = setTimeout(run, 70);

    return () => {
      cancelled = true;
      clearTimeout(t);
      unlockScroll();
    };
  }, [step, cur.target, cur.side, lockScroll, unlockScroll]);

  // Recompute on resize
  useEffect(() => {
    const h = () => {
      if (!cur.target || !cur.side || !layout?.targetRect) return;
      const el = document.querySelector(cur.target);
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width === 0) return;
      const vw = window.innerWidth, vh = window.innerHeight;
      const placed = placeTooltip({ x: r.left, y: r.top, width: r.width, height: r.height }, cur.side, vw, vh);
      setLayout(l => ({ ...l, ...placed }));
    };
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [cur.target, cur.side, layout?.targetRect]);

  // Final cleanup on unmount
  useEffect(() => () => unlockScroll(), [unlockScroll]);

  const next = () => step < STEPS.length - 1 ? setStep(s => s + 1) : done();
  const back = () => step > 0 && setStep(s => s - 1);

  if (!layout) return null;
  const { center, targetRect, tx, ty, TW, arrow } = layout;

  const tooltipStyle = center
    ? {
        position: "fixed", left: "50%", top: "50%", width: layout.TW,
        transform: visible ? "translate(-50%,-50%) scale(1)" : "translate(-50%,-50%) scale(0.87)",
        opacity: visible ? 1 : 0,
        transition: "opacity 0.4s cubic-bezier(0.22,1,0.36,1), transform 0.4s cubic-bezier(0.22,1,0.36,1)",
      }
    : {
        position: "fixed", left: tx, top: ty, width: TW,
        transform: visible ? "scale(1)" : "scale(0.93)",
        opacity: visible ? 1 : 0,
        transition: "opacity 0.4s cubic-bezier(0.22,1,0.36,1), transform 0.4s cubic-bezier(0.22,1,0.36,1)",
      };

  return (
    <>
      <style>{`
        @keyframes _tr { 0%,100%{box-shadow:0 0 0 4px rgba(225,48,108,.22),0 0 28px rgba(225,48,108,.32)} 50%{box-shadow:0 0 0 10px rgba(225,48,108,.06),0 0 48px rgba(225,48,108,.55)} }
        @keyframes _ta { from{stroke-dashoffset:1200;opacity:0} to{stroke-dashoffset:0;opacity:1} }
      `}</style>

      {/* ── Overlay ── */}
      <div style={{ position:"fixed", inset:0, zIndex:10000, pointerEvents:"none" }}>

        {/* Backdrop */}
        {center ? (
          <div style={{
            position:"absolute", inset:0, pointerEvents:"auto",
            background:"rgba(0,0,0,0.9)", backdropFilter:"blur(4px)",
            WebkitBackdropFilter:"blur(4px)",
            opacity: visible ? 1 : 0, transition:"opacity 0.32s ease",
          }} onClick={done} />
        ) : (
          <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%" }}>
            <defs>
              <mask id="_tm">
                <rect width="100%" height="100%" fill="white" />
                {targetRect && (
                  <rect
                    x={targetRect.x - SPOT_PAD} y={targetRect.y - SPOT_PAD}
                    width={targetRect.width + SPOT_PAD * 2}
                    height={targetRect.height + SPOT_PAD * 2}
                    rx="13" fill="black"
                  />
                )}
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.9)" mask="url(#_tm)"
              style={{ opacity: visible ? 1 : 0, transition:"opacity 0.32s ease" }} />
            <rect width="100%" height="100%" fill="transparent"
              style={{ pointerEvents:"auto", cursor:"pointer" }} onClick={done} />
          </svg>
        )}

        {/* Spotlight ring */}
        {targetRect && !center && (
          <div style={{
            position:"fixed",
            left: targetRect.x - SPOT_PAD - 2, top: targetRect.y - SPOT_PAD - 2,
            width: targetRect.width + (SPOT_PAD + 2) * 2,
            height: targetRect.height + (SPOT_PAD + 2) * 2,
            borderRadius: 15, border: "2px solid rgba(225,48,108,.9)",
            animation: "_tr 2s ease-in-out infinite",
            pointerEvents:"none",
            opacity: visible ? 1 : 0, transition:"opacity 0.32s ease",
          }} />
        )}

        {/* Curved arrow */}
        {arrow && visible && !center && (
          <svg style={{
            position:"fixed", inset:0, width:"100%", height:"100%",
            pointerEvents:"none", overflow:"visible", zIndex:1,
          }}>
            <defs>
              <marker id="_tah" markerWidth="14" markerHeight="11" refX="12" refY="5.5" orient="auto">
                <path d="M 0 1.5 Q 6 5.5 0 9.5 L 14 5.5 Z" fill="rgba(255,255,255,.95)" />
              </marker>
              <filter id="_tglow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="3.5" result="b"/>
                <feFlood floodColor="#E1306C" floodOpacity=".7" result="c"/>
                <feComposite in="c" in2="b" operator="in" result="g"/>
                <feMerge><feMergeNode in="g"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
            </defs>
            <path d={arrow} fill="none" stroke="rgba(255,255,255,.92)" strokeWidth="2.6"
              strokeLinecap="round" markerEnd="url(#_tah)" strokeDasharray="1200"
              filter="url(#_tglow)"
              style={{ animation:"_ta .85s cubic-bezier(.22,1,.36,1) both" }} />
          </svg>
        )}
      </div>

      {/* ── Tooltip card ── */}
      <div
        style={{
          ...tooltipStyle,
          zIndex: 10001,
          background: "var(--bg-secondary)",
          border: "1px solid rgba(225,48,108,.2)",
          borderRadius: 22,
          padding: "20px 22px 18px",
          boxShadow: "0 28px 80px rgba(0,0,0,.65),0 0 0 1px rgba(255,255,255,.04),inset 0 1px 0 rgba(255,255,255,.07)",
          pointerEvents: "auto",
        }}
        dir={isRtl ? "rtl" : "ltr"}
        onClick={e => e.stopPropagation()}
      >
        {/* Step dots + close */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <div style={{ display:"flex", gap:5, alignItems:"center" }}>
            {STEPS.map((_, i) => (
              <div key={i} style={{
                height:5, borderRadius:3,
                width: i === step ? 20 : 6,
                background: i === step ? "linear-gradient(90deg,#E1306C,#833AB4)"
                  : i < step ? "rgba(225,48,108,.42)" : "var(--surface-3)",
                transition: "all .38s cubic-bezier(.22,1,.36,1)",
              }} />
            ))}
          </div>
          <button onClick={done} style={{
            width:28, height:28, borderRadius:8, background:"var(--surface-3)",
            border:"none", cursor:"pointer", display:"flex", alignItems:"center",
            justifyContent:"center", color:"var(--text-secondary)", flexShrink:0,
          }}>
            <X size={13}/>
          </button>
        </div>

        {/* Text */}
        <div style={{ marginBottom:18 }}>
          <div style={{ fontWeight:800, fontSize:15, color:"var(--text-primary)", marginBottom:7, lineHeight:1.35 }}>
            {cur.content[lang]?.title || cur.content.en.title}
          </div>
          <div style={{ fontSize:13, color:"var(--text-secondary)", lineHeight:1.62 }}>
            {cur.content[lang]?.desc || cur.content.en.desc}
          </div>
        </div>

        {/* Nav buttons */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
          {step > 0 ? (
            <button onClick={back} style={{
              padding:"8px 14px", borderRadius:10, background:"var(--surface-3)",
              border:"none", cursor:"pointer", fontSize:12, color:"var(--text-secondary)",
              display:"flex", alignItems:"center", gap:4,
            }}>
              {isRtl ? <ChevronRight size={13}/> : <ChevronLeft size={13}/>}
              {t("common.back", lang)}
            </button>
          ) : (
            <button onClick={done} style={{
              padding:"8px 14px", borderRadius:10, background:"none",
              border:"none", cursor:"pointer", fontSize:12, color:"var(--text-secondary)",
            }}>
              {t("DashboardV2.skip", lang)}
            </button>
          )}

          <button onClick={next} style={{
            padding:"10px 22px", borderRadius:12,
            background:"linear-gradient(135deg,#E1306C,#833AB4)",
            border:"none", cursor:"pointer", fontSize:13, fontWeight:700, color:"white",
            display:"flex", alignItems:"center", gap:5,
            boxShadow:"0 6px 22px rgba(225,48,108,.4)",
            WebkitTapHighlightColor:"transparent",
          }}>
            {step === STEPS.length - 1
              ? t("Dashboard.let_s_go", lang)
              : t("Admin.next", lang)}
            {step < STEPS.length - 1 && (isRtl ? <ChevronLeft size={14}/> : <ChevronRight size={14}/>)}
          </button>
        </div>
      </div>
    </>
  );
}
